import type { ModalSession } from './session'
import type { CrosschainBuyViewState } from './view-state'

export interface ReadonlyValue<T> {
  readonly value: T
}

export interface BuyFlowController {
  readonly state: ReadonlyValue<CrosschainBuyViewState>
  readonly clock5s: ReadonlyValue<number>
  readonly modal: ModalSession
  mountUi: (track: (eventName: string, payload?: Record<string, unknown>) => void) => void
  unmountUi: () => void
  acquireClock5s: () => void
  releaseClock5s: () => void
  endExecution: () => Promise<boolean>
  recordDiagnostic: (event: string, data?: Record<string, unknown>) => void
}

interface CreateBuyFlowControllerDependencies {
  state: ReadonlyValue<CrosschainBuyViewState>
  clock5s: ReadonlyValue<number>
  modal: ModalSession
  setAnalyticsClient: (
    track: (eventName: string, payload?: Record<string, unknown>) => void
  ) => void
  resetAnalyticsClient: () => void
  wireTonRecovery: () => void
  disposeTonRecovery: () => void
  acquireClock5s: () => void
  releaseClock5s: () => void
  endExecution: () => Promise<boolean>
  recordDiagnostic: (event: string, data?: Record<string, unknown>) => void
}

const readonlyValue = <T>(source: ReadonlyValue<T>): ReadonlyValue<T> =>
  Object.freeze({
    get value(): T {
      return source.value
    },
  })

export const createBuyFlowController = (
  deps: CreateBuyFlowControllerDependencies
): BuyFlowController =>
  Object.freeze({
    state: readonlyValue(deps.state),
    clock5s: readonlyValue(deps.clock5s),
    modal: deps.modal,
    mountUi: (track: (eventName: string, payload?: Record<string, unknown>) => void) => {
      deps.setAnalyticsClient(track)
      deps.wireTonRecovery()
    },
    unmountUi: () => {
      deps.resetAnalyticsClient()
      deps.disposeTonRecovery()
    },
    acquireClock5s: deps.acquireClock5s,
    releaseClock5s: deps.releaseClock5s,
    endExecution: deps.endExecution,
    recordDiagnostic: deps.recordDiagnostic,
  })

let installedController: BuyFlowController | null = null

export const installBuyFlowController = (controller: BuyFlowController): void => {
  installedController = controller
}

export const getBuyFlowController = (): BuyFlowController => {
  if (!installedController) throw new Error('crosschain_buy_flow_not_installed')
  return installedController
}
